import { useState, useEffect, useRef, useContext } from "react";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import mergeTicketContact from "../../utils/mergeTicketContact";

import api from "../../services/api";

const newAvatarBatchId = () => {
  if (!window.crypto?.getRandomValues) return undefined;
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
};

const useTickets = ({
  searchParam,
  searchScope,
  tags,
  users,
  pageNumber,
  status,
  date,
  updatedAt,
  showAll,
  queueIds,
  withUnreadMessages,
  refreshToken = 0,
  enabled = true,
  prefetchNextPage = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [count, setCount] = useState(0);
  const [avatarRefreshEnabled, setAvatarRefreshEnabled] = useState(false);
  const socketManager = useContext(SocketContext);
  const lookahead = useRef(null);
  const foregroundContacts = useRef(null);
  const avatarBatches = useRef(new Set());
  const avatarPromotionEnabled = useRef(false);
  const companyId = localStorage.getItem("companyId");
  const scopeKey = JSON.stringify([companyId, searchParam, searchScope, tags, users, status,
    date, updatedAt, showAll, queueIds, withUnreadMessages, refreshToken]);

  useEffect(() => () => {
    if (lookahead.current) lookahead.current.controller.abort();
    lookahead.current = null;
    const batchIds = Array.from(avatarBatches.current).slice(-80);
    avatarBatches.current.clear();
    if (avatarPromotionEnabled.current && batchIds.length) {
      api.post("/tickets/avatar-pages/cancel", { batchIds }, { timeout: 1500 }).catch(() => {});
    }
    avatarPromotionEnabled.current = false;
  }, [scopeKey, enabled, prefetchNextPage]);

  useEffect(() => {
    if (!prefetchNextPage || !enabled || !socketManager) return undefined;
    const socket = socketManager.getSocket(companyId);
    const invalidate = () => { if (lookahead.current) lookahead.current.invalidated = true; };
    socket.on(`company-${companyId}-ticket`, invalidate);
    socket.on(`company-${companyId}-appMessage`, invalidate);
    socket.on("ready", invalidate);
    socket.on(`company-${companyId}-contact`, data => {
      if (data.action === "update" && data.contact?.id) {
        for (const contacts of [lookahead.current?.contacts, foregroundContacts.current]) {
          if (!contacts) continue;
          if (contacts.size >= 100) contacts.delete(contacts.keys().next().value);
          contacts.set(data.contact.id, data.contact);
        }
      }
    });
    return () => socket.disconnect();
  }, [companyId, enabled, prefetchNextPage, socketManager]);

  useEffect(() => {
    if (pageNumber === 1) {
      setTickets([]);
      setHasMore(false);
      setCount(0);
    }
  }, [
    searchParam,
    searchScope,
    tags,
    users,
    status,
    date,
    updatedAt,
    showAll,
    queueIds,
    withUnreadMessages,
    pageNumber,
    refreshToken,
    enabled,
  ]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setAvatarRefreshEnabled(false);
      return () => {};
    }

    setLoading(true);
    const shouldDebounceSearch = Boolean(searchParam && searchParam.trim().length > 0);
    const debounceDelay = shouldDebounceSearch ? 150 : 0;
    const controller = new AbortController();
    const contactUpdates = new Map();
    foregroundContacts.current = contactUpdates;
    let didCancel = false;
    let prefetchTimer;
    const avatarBatchId = newAvatarBatchId();

    const delayDebounceFn = setTimeout(() => {
      const fetchTickets = async () => {
        const startTime = performance.now();
        const logEnabled = process.env.NODE_ENV === "development";

        try {
          const params = {
            searchParam,
            searchScope,
            pageNumber,
            tags,
            users,
            status,
            date,
            updatedAt,
            showAll,
            queueIds,
            withUnreadMessages,
            avatarPageRole: "visible",
            avatarBatchId,
            avatarPrefetchExpected: Boolean(prefetchNextPage && !shouldDebounceSearch && document.visibilityState !== "hidden"),
          };
          const entry = lookahead.current;
          let data;
          let promotion = Promise.resolve(true);
          if (entry && entry.scopeKey === scopeKey && entry.page === pageNumber
            && !entry.invalidated && Date.now() - entry.createdAt < 15000) {
            data = await entry.promise;
            if (entry.invalidated) data = null;
            if (data) data = { ...data, tickets: data.tickets.map(ticket =>
              mergeTicketContact(ticket, entry.contacts.get(ticket.contactId))) };
            if (data && data.avatarPromotionEnabled && entry.avatarBatchId && avatarBatchId) {
              promotion = api.post("/tickets/avatar-pages/promote", {
                previousBatchId: entry.avatarBatchId, batchId: avatarBatchId, pageNumber,
                prefetchExpected: Boolean(prefetchNextPage && !shouldDebounceSearch && document.visibilityState !== "hidden"),
              }, { timeout: 1500 }).then(() => true).catch(() => false);
            }
          }
          if (didCancel) return;
          if (!data) {
            if (entry) entry.controller.abort();
            ({ data } = await api.get("/tickets", { params, signal: controller.signal }));
          }
          if (lookahead.current === entry) lookahead.current = null;

          if (didCancel) {
            return;
          }

          setTickets(data.tickets.map(ticket => mergeTicketContact(ticket, contactUpdates.get(ticket.contactId))));
          setAvatarRefreshEnabled(Boolean(data.avatarPromotionEnabled));
          if (data.avatarPromotionEnabled && avatarBatchId) {
            avatarPromotionEnabled.current = true;
            if (avatarBatches.current.size >= 80) avatarBatches.current.delete(avatarBatches.current.values().next().value);
            avatarBatches.current.add(avatarBatchId);
          }
          setHasMore(data.hasMore);
          setCount(data.count || 0);
          setLoading(false);

          // Only one page ahead, after the current page is visible. Never chain from a prefetch.
          if (prefetchNextPage && data.hasMore && !shouldDebounceSearch) {
            promotion.then(promoted => {
              if (didCancel || !promoted) return;
              prefetchTimer = setTimeout(() => {
                if (didCancel) return;
                if (document.visibilityState === "hidden") {
                  if (data.avatarPromotionEnabled && avatarBatchId) {
                    api.post("/tickets/avatar-pages/no-prefetch", { batchId: avatarBatchId }, { timeout: 1500 }).catch(() => {});
                  }
                  return;
                }
                const next = { scopeKey, page: Number(pageNumber) + 1, createdAt: Date.now(),
                  controller: new AbortController(), contacts: new Map(), invalidated: false, avatarBatchId };
                if (lookahead.current) lookahead.current.controller.abort();
                lookahead.current = next;
                next.promise = api.get("/tickets", {
                  params: { ...params, pageNumber: next.page, avatarPageRole: "prefetch" }, signal: next.controller.signal, timeout: 10000,
                }).then(response => response.data).catch(() => null);
              }, 750);
            });
          }

          if (logEnabled) {
            const duration = performance.now() - startTime;
            // eslint-disable-next-line no-console
            console.debug("[TIMING] useTickets", {
              durationMs: Number(duration.toFixed(2)),
              pageNumber,
              count: data.count,
              hasMore: data.hasMore,
              searchParam,
              searchScope,
            });
          }
        } catch (err) {
          if (didCancel || err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
            return;
          }

          setLoading(false);

          if (logEnabled) {
            const duration = performance.now() - startTime;
            // eslint-disable-next-line no-console
            console.debug("[TIMING] useTickets.error", {
              durationMs: Number(duration.toFixed(2)),
              pageNumber,
              searchParam,
              searchScope,
              message: err?.message,
            });
          }

          toastError(err);
        }
      };
      fetchTickets();
    }, debounceDelay);
    return () => {
      didCancel = true;
      controller.abort();
      clearTimeout(delayDebounceFn);
      clearTimeout(prefetchTimer);
      if (foregroundContacts.current === contactUpdates) foregroundContacts.current = null;
    };
  }, [
    searchParam,
    searchScope,
    tags,
    users,
    pageNumber,
    status,
    date,
    updatedAt,
    showAll,
    queueIds,
    withUnreadMessages,
    refreshToken,
    enabled,
    prefetchNextPage,
    scopeKey,
  ]);

  return { tickets, loading, hasMore, count, avatarRefreshEnabled };
};

export default useTickets;
